import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './dialog';
import { Button } from './button';
import { ScrollArea } from './scroll-area';
import { Badge } from './badge';
import { Check, FileText, Shield, Cookie, ScrollText, AlertTriangle } from 'lucide-react';

export type DocumentType = 'privacy_policy' | 'cookie_policy' | 'terms_of_service' | 'disclaimer';

interface Template {
  id: string;
  name: string;
  description: string;
  style: 'formal' | 'semplice' | 'dettagliato';
  content: string;
}

// Template predefiniti per ogni tipo di documento
const TEMPLATES: Record<DocumentType, Template[]> = {
  privacy_policy: [
    {
      id: 'privacy_formal',
      name: 'Formale Completo',
      description: 'Privacy policy completa con tutti i riferimenti GDPR',
      style: 'formal',
      content: `<h1>Informativa sulla Privacy</h1>
<p><em>ai sensi dell'art. 13 del Regolamento UE 2016/679 (GDPR)</em></p>
<p><strong>Ultimo aggiornamento:</strong> {{data_oggi}}</p>

<h2>1. Titolare del Trattamento</h2>
<p>Il Titolare del trattamento dei dati personali è <strong>{{nome_associazione}}</strong>, con sede in {{indirizzo_associazione}}, P.IVA {{partita_iva}}, email {{email_associazione}}, telefono {{telefono_associazione}}.</p>

<h2>2. Tipologie di Dati Raccolti</h2>
<p>Il Titolare raccoglie le seguenti categorie di dati personali:</p>
<ul>
<li><strong>Dati identificativi</strong>: nome, cognome, codice fiscale, data di nascita</li>
<li><strong>Dati di contatto</strong>: indirizzo email, numero di telefono, indirizzo postale</li>
<li><strong>Dati aziendali</strong>: ragione sociale, partita IVA, sede legale</li>
<li><strong>Dati di navigazione</strong>: indirizzo IP, browser utilizzato, pagine visitate</li>
<li><strong>Dati transazionali</strong>: storico ordini, preferenze di acquisto, metodi di pagamento</li>
</ul>

<h2>3. Finalità del Trattamento</h2>
<p>I dati personali sono trattati per le seguenti finalità:</p>
<ul>
<li><strong>Esecuzione contrattuale</strong>: gestione dell'iscrizione, partecipazione agli acquisti collettivi</li>
<li><strong>Obblighi legali</strong>: adempimenti fiscali e contabili</li>
<li><strong>Legittimo interesse</strong>: prevenzione frodi, miglioramento servizi</li>
<li><strong>Consenso</strong>: invio comunicazioni promozionali (se autorizzato)</li>
</ul>

<h2>4. Base Giuridica</h2>
<p>Il trattamento è fondato su:</p>
<ul>
<li>Art. 6(1)(b) GDPR - Esecuzione di un contratto</li>
<li>Art. 6(1)(c) GDPR - Obbligo legale</li>
<li>Art. 6(1)(f) GDPR - Legittimo interesse</li>
<li>Art. 6(1)(a) GDPR - Consenso dell'interessato</li>
</ul>

<h2>5. Destinatari dei Dati</h2>
<p>I dati possono essere comunicati a:</p>
<ul>
<li>Fornitori partner per l'esecuzione degli ordini</li>
<li>Responsabili del trattamento (hosting, email, pagamenti)</li>
<li>Autorità competenti su richiesta</li>
</ul>

<h2>6. Trasferimento Dati</h2>
<p>I dati non sono trasferiti al di fuori dello Spazio Economico Europeo.</p>

<h2>7. Periodo di Conservazione</h2>
<p>I dati sono conservati per:</p>
<ul>
<li>Dati contrattuali: 10 anni dalla cessazione del rapporto</li>
<li>Dati fiscali: secondo obblighi di legge</li>
<li>Dati di navigazione: 12 mesi</li>
</ul>

<h2>8. Diritti dell'Interessato</h2>
<p>Ai sensi degli artt. 15-22 GDPR, l'interessato ha diritto di:</p>
<ul>
<li>Accedere ai propri dati</li>
<li>Ottenere la rettifica o cancellazione</li>
<li>Limitare il trattamento</li>
<li>Opporsi al trattamento</li>
<li>Richiedere la portabilità</li>
<li>Revocare il consenso</li>
<li>Proporre reclamo al Garante Privacy</li>
</ul>

<h2>9. Contatti</h2>
<p>Per esercitare i diritti o per informazioni: <strong>{{email_associazione}}</strong></p>`
    },
    {
      id: 'privacy_simple',
      name: 'Semplice',
      description: 'Privacy policy essenziale e di facile comprensione',
      style: 'semplice',
      content: `<h1>Privacy Policy</h1>
<p><strong>{{nome_associazione}}</strong> rispetta la tua privacy.</p>

<h2>Cosa raccogliamo</h2>
<p>Raccogliamo solo i dati necessari per offrirti i nostri servizi:</p>
<ul>
<li>Nome e contatti per comunicare con te</li>
<li>Dati aziendali per la fatturazione</li>
<li>Storico ordini per gestire gli acquisti</li>
</ul>

<h2>Come usiamo i tuoi dati</h2>
<ul>
<li>Per permetterti di partecipare ai Groupage</li>
<li>Per inviarti comunicazioni sugli ordini</li>
<li>Per adempiere agli obblighi fiscali</li>
</ul>

<h2>Con chi condividiamo</h2>
<p>Condividiamo i tuoi dati solo con i fornitori necessari per completare gli ordini.</p>

<h2>I tuoi diritti</h2>
<p>Puoi sempre chiederci di vedere, modificare o cancellare i tuoi dati scrivendo a {{email_associazione}}.</p>

<p><em>Ultimo aggiornamento: {{data_oggi}}</em></p>`
    },
    {
      id: 'privacy_ecommerce',
      name: 'E-commerce Groupage',
      description: 'Privacy ottimizzata per piattaforme di acquisto collettivo',
      style: 'dettagliato',
      content: `<h1>Privacy Policy - Piattaforma Groupage</h1>
<p><strong>{{nome_associazione}}</strong> - Piattaforma di Acquisto Collettivo</p>
<p><em>Ultimo aggiornamento: {{data_oggi}}</em></p>

<h2>Chi Siamo</h2>
<p>Siamo <strong>{{nome_associazione}}</strong>, un'associazione che facilita gli acquisti collettivi (Groupage) per i propri membri. Questa informativa spiega come trattiamo i tuoi dati personali.</p>

<h2>Dati che Raccogliamo</h2>
<h3>Dati di Registrazione</h3>
<ul>
<li>Nome, cognome, email, telefono</li>
<li>Dati aziendali (ragione sociale, P.IVA, indirizzo)</li>
</ul>

<h3>Dati di Utilizzo</h3>
<ul>
<li>Ordini effettuati e storico acquisti</li>
<li>Preferenze e liste desideri</li>
<li>Comunicazioni con fornitori e amministrazione</li>
</ul>

<h3>Dati Tecnici</h3>
<ul>
<li>Indirizzo IP, tipo di browser</li>
<li>Dati di navigazione sulla piattaforma</li>
</ul>

<h2>Come Utilizziamo i Dati</h2>
<ul>
<li><strong>Gestione ordini</strong>: processare le tue partecipazioni ai Groupage</li>
<li><strong>Comunicazioni</strong>: aggiornamenti su ordini, nuove opportunità</li>
<li><strong>Fatturazione</strong>: emissione documenti fiscali</li>
<li><strong>Miglioramento servizio</strong>: analisi anonime per ottimizzare la piattaforma</li>
</ul>

<h2>Condivisione Dati</h2>
<p>I tuoi dati sono condivisi solo con:</p>
<ul>
<li><strong>Fornitori partner</strong>: per la consegna degli ordini</li>
<li><strong>Servizi tecnici</strong>: hosting e gestione piattaforma</li>
</ul>
<p>Non vendiamo mai i tuoi dati a terzi.</p>

<h2>I Tuoi Diritti</h2>
<p>Puoi in qualsiasi momento:</p>
<ul>
<li>Accedere ai tuoi dati</li>
<li>Richiedere correzioni</li>
<li>Cancellare il tuo account</li>
<li>Esportare i tuoi dati</li>
</ul>

<h2>Contatti</h2>
<p>Per qualsiasi richiesta: <strong>{{email_associazione}}</strong></p>
<p>{{indirizzo_associazione}} | Tel: {{telefono_associazione}}</p>`
    }
  ],
  cookie_policy: [
    {
      id: 'cookie_standard',
      name: 'Standard',
      description: 'Cookie policy completa con categorie',
      style: 'formal',
      content: `<h1>Cookie Policy</h1>
<p><strong>Ultimo aggiornamento:</strong> {{data_oggi}}</p>

<h2>Cosa sono i Cookie</h2>
<p>I cookie sono piccoli file di testo salvati sul tuo dispositivo durante la navigazione.</p>

<h2>Cookie che utilizziamo</h2>

<h3>Cookie Tecnici (Necessari)</h3>
<p>Essenziali per il funzionamento del sito:</p>
<ul>
<li><strong>Sessione</strong>: mantengono il login attivo</li>
<li><strong>Preferenze</strong>: ricordano le tue scelte</li>
<li><strong>Sicurezza</strong>: proteggono da attacchi</li>
</ul>

<h3>Cookie Analitici</h3>
<p>Ci aiutano a migliorare il servizio (in forma anonima):</p>
<ul>
<li>Pagine più visitate</li>
<li>Tempo di navigazione</li>
<li>Eventuali errori</li>
</ul>

<h2>Gestione Cookie</h2>
<p>Puoi gestire i cookie dal banner iniziale o dalle impostazioni del browser.</p>

<h2>Contatti</h2>
<p>Per domande: {{email_associazione}}</p>`
    },
    {
      id: 'cookie_minimal',
      name: 'Minimale',
      description: 'Solo informazioni essenziali',
      style: 'semplice',
      content: `<h1>Cookie</h1>
<p>Questo sito usa cookie tecnici per funzionare correttamente e cookie analitici per migliorare la tua esperienza.</p>
<p>Puoi gestire le preferenze dal banner cookie o dal tuo browser.</p>
<p>Contatto: {{email_associazione}}</p>`
    }
  ],
  terms_of_service: [
    {
      id: 'terms_complete',
      name: 'Completo',
      description: 'Termini dettagliati con tutte le clausole',
      style: 'dettagliato',
      content: `<h1>Termini e Condizioni di Utilizzo</h1>
<p><strong>{{nome_associazione}}</strong></p>
<p><em>Ultimo aggiornamento: {{data_oggi}}</em></p>

<h2>1. Accettazione</h2>
<p>L'utilizzo della piattaforma implica l'accettazione integrale dei presenti Termini.</p>

<h2>2. Definizioni</h2>
<ul>
<li><strong>"Piattaforma"</strong>: il sito web e i servizi di {{nome_associazione}}</li>
<li><strong>"Utente"</strong>: l'associato registrato</li>
<li><strong>"Groupage"</strong>: opportunità di acquisto collettivo</li>
<li><strong>"Fornitore"</strong>: venditore dei prodotti/servizi</li>
</ul>

<h2>3. Accesso e Registrazione</h2>
<ul>
<li>L'accesso è riservato agli associati di {{nome_associazione}}</li>
<li>L'Utente è responsabile delle proprie credenziali</li>
<li>È vietata la condivisione dell'account</li>
</ul>

<h2>4. Servizi Offerti</h2>
<p>La piattaforma permette di:</p>
<ul>
<li>Visualizzare opportunità di acquisto collettivo</li>
<li>Partecipare ai Groupage attivi</li>
<li>Gestire ordini e pagamenti</li>
<li>Comunicare con l'amministrazione</li>
</ul>

<h2>5. Ordini e Pagamenti</h2>
<ul>
<li>Gli ordini sono vincolanti una volta confermati</li>
<li>I prezzi includono IVA salvo diversa indicazione</li>
<li>Il pagamento avviene secondo le modalità indicate</li>
<li>La fatturazione è a cura del Fornitore</li>
</ul>

<h2>6. Responsabilità</h2>
<ul>
<li>{{nome_associazione}} facilita l'incontro tra Utenti e Fornitori</li>
<li>La responsabilità per i prodotti è del Fornitore</li>
<li>{{nome_associazione}} non è parte del contratto di vendita</li>
</ul>

<h2>7. Proprietà Intellettuale</h2>
<p>Tutti i contenuti sono protetti da copyright. È vietata la riproduzione non autorizzata.</p>

<h2>8. Privacy</h2>
<p>Il trattamento dei dati avviene secondo la Privacy Policy.</p>

<h2>9. Modifiche</h2>
<p>{{nome_associazione}} può modificare i Termini con preavviso agli Utenti.</p>

<h2>10. Legge Applicabile</h2>
<p>I presenti Termini sono regolati dalla legge italiana. Foro competente: tribunale della sede di {{nome_associazione}}.</p>

<h2>11. Contatti</h2>
<p>{{email_associazione}} | {{telefono_associazione}}</p>`
    },
    {
      id: 'terms_essential',
      name: 'Essenziale',
      description: 'Termini brevi e chiari',
      style: 'semplice',
      content: `<h1>Termini di Utilizzo</h1>

<h2>Cos'è questa piattaforma</h2>
<p>{{nome_associazione}} offre ai suoi associati la possibilità di partecipare ad acquisti collettivi (Groupage) per ottenere condizioni migliori.</p>

<h2>Come funziona</h2>
<ol>
<li>Consulti le opportunità disponibili</li>
<li>Partecipi a quelle che ti interessano</li>
<li>Paghi secondo le modalità indicate</li>
<li>Ricevi i prodotti dal fornitore</li>
</ol>

<h2>Regole importanti</h2>
<ul>
<li>Gli ordini confermati sono vincolanti</li>
<li>L'account è personale e non cedibile</li>
<li>I prodotti sono venduti dai fornitori, non da noi</li>
</ul>

<h2>Domande?</h2>
<p>Scrivici a {{email_associazione}}</p>`
    },
    {
      id: 'terms_marketplace',
      name: 'Marketplace Groupage',
      description: 'Termini specifici per marketplace di acquisti collettivi',
      style: 'dettagliato',
      content: `<h1>Termini e Condizioni - Marketplace Groupage</h1>
<p><strong>{{nome_associazione}}</strong></p>
<p><em>Versione del {{data_oggi}}</em></p>

<h2>1. Il Nostro Servizio</h2>
<p><strong>{{nome_associazione}}</strong> è una piattaforma che aggrega la domanda dei suoi membri per ottenere condizioni di acquisto vantaggiose attraverso il meccanismo del Groupage (acquisto collettivo).</p>

<h2>2. Come Funziona il Groupage</h2>
<ol>
<li><strong>Pubblicazione</strong>: Pubblichiamo opportunità di acquisto con quantità minime e prezzi speciali</li>
<li><strong>Adesione</strong>: I membri aderiscono indicando le quantità desiderate</li>
<li><strong>Chiusura</strong>: Al raggiungimento del minimo, il Groupage si chiude</li>
<li><strong>Ordine</strong>: L'ordine cumulativo viene inviato al fornitore</li>
<li><strong>Consegna</strong>: I prodotti vengono consegnati secondo modalità concordate</li>
</ol>

<h2>3. Impegno dell'Utente</h2>
<ul>
<li>L'adesione a un Groupage costituisce impegno vincolante</li>
<li>Il pagamento deve avvenire entro i termini indicati</li>
<li>Non è possibile annullare dopo la chiusura del Groupage</li>
</ul>

<h2>4. Ruolo dell'Associazione</h2>
<p><strong>{{nome_associazione}}</strong>:</p>
<ul>
<li>Seleziona i fornitori partner</li>
<li>Negozia le condizioni di acquisto</li>
<li>Gestisce la raccolta ordini</li>
<li>Facilita la comunicazione</li>
</ul>
<p><em>Non è venditore diretto dei prodotti.</em></p>

<h2>5. Garanzie e Resi</h2>
<p>Le garanzie sui prodotti sono quelle previste dalla legge e offerte dal fornitore. Per resi e reclami, contattare {{email_associazione}}.</p>

<h2>6. Contatti</h2>
<p>{{email_associazione}} | {{telefono_associazione}}</p>
<p>{{indirizzo_associazione}}</p>`
    }
  ],
  disclaimer: [
    {
      id: 'disclaimer_full',
      name: 'Completo',
      description: 'Disclaimer dettagliato con esclusioni',
      style: 'formal',
      content: `<h1>Disclaimer</h1>
<p><strong>{{nome_associazione}}</strong></p>

<h2>Natura del Servizio</h2>
<p>Questa piattaforma è uno strumento di facilitazione per gli acquisti collettivi. {{nome_associazione}} opera come intermediario tra gli associati e i fornitori.</p>

<h2>Limitazione di Responsabilità</h2>
<ul>
<li>Le informazioni sui prodotti sono fornite dai rispettivi fornitori</li>
<li>{{nome_associazione}} non garantisce l'accuratezza delle informazioni di terze parti</li>
<li>Prezzi, disponibilità e caratteristiche possono variare</li>
<li>{{nome_associazione}} non è responsabile per ritardi o problemi di consegna</li>
</ul>

<h2>Rapporto Contrattuale</h2>
<p>Il contratto di vendita si perfeziona direttamente tra l'Utente e il Fornitore. {{nome_associazione}} non è parte di tale contratto.</p>

<h2>Garanzie</h2>
<p>Le garanzie sui prodotti sono quelle previste per legge e offerte dal Fornitore.</p>

<h2>Contenuti Utente</h2>
<p>Gli utenti sono responsabili dei contenuti che pubblicano sulla piattaforma.</p>

<h2>Modifiche</h2>
<p>{{nome_associazione}} si riserva il diritto di modificare questo disclaimer in qualsiasi momento.</p>

<h2>Contatti</h2>
<p>Per chiarimenti: {{email_associazione}}</p>`
    },
    {
      id: 'disclaimer_short',
      name: 'Breve',
      description: 'Disclaimer sintetico',
      style: 'semplice',
      content: `<h1>Disclaimer</h1>
<p>{{nome_associazione}} facilita gli acquisti collettivi ma non vende direttamente i prodotti.</p>
<p>Le informazioni sui prodotti provengono dai fornitori. Verifica sempre i dettagli prima dell'acquisto.</p>
<p>Per problemi con un ordine, contatta il fornitore o scrivi a {{email_associazione}}.</p>`
    }
  ]
};

const DOCUMENT_ICONS: Record<DocumentType, React.ElementType> = {
  privacy_policy: Shield,
  cookie_policy: Cookie,
  terms_of_service: ScrollText,
  disclaimer: AlertTriangle,
};

const DOCUMENT_LABELS: Record<DocumentType, string> = {
  privacy_policy: 'Privacy Policy',
  cookie_policy: 'Cookie Policy',
  terms_of_service: 'Termini e Condizioni',
  disclaimer: 'Disclaimer',
};

const STYLE_COLORS: Record<string, string> = {
  formal: 'bg-blue-100 text-blue-800',
  semplice: 'bg-green-100 text-green-800',
  dettagliato: 'bg-purple-100 text-purple-800',
};

interface TemplateLibraryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentType: DocumentType;
  onSelectTemplate: (content: string) => void;
}

export function TemplateLibrary({
  open,
  onOpenChange,
  documentType,
  onSelectTemplate,
}: TemplateLibraryProps) {
  const templates = TEMPLATES[documentType] || [];
  const Icon = DOCUMENT_ICONS[documentType];
  const label = DOCUMENT_LABELS[documentType];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5" />
            Biblioteca Template - {label}
          </DialogTitle>
          <DialogDescription>
            Scegli un template predefinito per iniziare. Le variabili come {`{{nome_associazione}}`} verranno sostituite automaticamente.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-3 pr-4">
            {templates.map((template) => (
              <div
                key={template.id}
                className="border rounded-lg p-4 hover:border-primary/50 hover:bg-muted/30 transition-colors cursor-pointer group"
                onClick={() => {
                  onSelectTemplate(template.content);
                  onOpenChange(false);
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium">{template.name}</h4>
                      <Badge variant="secondary" className={STYLE_COLORS[template.style]}>
                        {template.style}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">{template.description}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <Check className="h-4 w-4 mr-1" />
                    Usa
                  </Button>
                </div>
                
                {/* Preview */}
                <div className="mt-3 p-3 bg-muted/50 rounded text-xs text-muted-foreground max-h-24 overflow-hidden relative">
                  <div dangerouslySetInnerHTML={{ __html: template.content.substring(0, 300) + '...' }} />
                  <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-muted/50 to-transparent" />
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default TemplateLibrary;
